import { useContext, useEffect } from 'react';
import type { ProjectBoardSprints, ProjectsResponse, Subproject } from '../../api/projects-client/projects-client.type';
import { useProjectBoardSprints } from '../../api/sprints-client/sprints-client.hooks';
import { usePortfolioProjects } from '../../api/summary-client/summary-client.hooks';
import { GlobalFilterContext } from '../../contexts/global-filter';
import { useGlobalStore } from '../../store/global-store/global-store';

/**
 * Custom hook that retrieves teams based on the portfolio and sets the team in the global filter context.
 *
 * @return {Object} An object containing an array of teams.
 */
const useTeams = (): { teams: ProjectsResponse[]; boards: Subproject[] } => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const { team, setTeam, setBoard } = useContext(GlobalFilterContext);

  const { projects: teams = [] } = usePortfolioProjects(portfolio?.id, { enabled: !!portfolio, staleTime: Infinity });
  useEffect(() => {
    if (portfolio) {
      setTeam(teams[0] || null);
      setBoard(teams[0]?.subprojects[0] || null);
    }
  }, [portfolio, teams, setTeam, setBoard]);

  return { teams, boards: team?.subprojects || [] };
};

/**
 * Retrieves the board sprints data based on the selected team and sets the default board and sprint.
 *
 * @return {Object} An object containing the board sprints data or undefined.
 */
const useBoardSprintsData = (): { boardSprintsData: ProjectBoardSprints | undefined } => {
  const { team, board, setSprint } = useContext(GlobalFilterContext);

  const { data: boardSprintsData } = useProjectBoardSprints(team?.id, {
    queryKey: ['boardSprints', team?.id],
    enabled: !!team,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (boardSprintsData && board) {
      const boardSprints = boardSprintsData.subproject_sprints[board?.name];
      const defaultSprintData = boardSprints?.[0];
      setSprint(defaultSprintData?.[0] || null);
    }
  }, [boardSprintsData, setSprint, board]);

  return { boardSprintsData };
};

export { useBoardSprintsData, useTeams };
